// src/pages/CrowdStrike/ComplianceOverview.js

import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Input, Progress } from 'reactstrap';
import { Card, Container, Form, Spinner, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { crowdstrikeEnabledSelector } from '../../store/selectors/licenseSelectors';
import {
  setSelectedStandard,
  fetchCloudScopes,
  setSelectedCloudScope,
  resetCloudScopes,
} from '../../store/slices/crowdstrikeSlice';
import {
  DownloadCrowdStrikeIsoWord,
  DownloadCrowdStrikeCisWord,
  DownloadCrowdStrikeBestPractices,
} from '../../api/crowdstrikeApi';
import CloudScopeSelector from '../Cloud/components/CloudScopeSelector';
import { getCloudScopeConfig } from '../Cloud/cloudScopeConfig';
import toastAlert from '../../js/Base/ToastAlert';

function getProjectIdFromLocalStorage() {
  const raw = localStorage.getItem('project_id');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function ComplianceOverview({ platform }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const projectId = useMemo(() => getProjectIdFromLocalStorage(), []);

  const {
    selectedStandard,
    cloudScopes,
    selectedCloudScope,
    scopesLoading,
  } = useSelector((state) => state.crowdStrike);

  const authorityCodes = useSelector(state => state.businessMembers.authorityCodes);
  const crowdStrikeEnabled = useSelector(crowdstrikeEnabledSelector);

  const PLATFORM_CONFIG = {
    azure: {
      label: 'Microsoft Azure',
      cloud: 'azure',
      standards: [
        { id: 'total', label: 'Microsoft Azure合規總表', apiKey: 'total' },
        { id: 'iso_27001', label: 'ISO 27001:2022', apiKey: 'ISO 27001:2022' },
        { id: 'cis', label: 'CIS 4.0.0 Azure Foundations', apiKey: 'CIS 4.0.0 Azure Foundations' },
        {
          id: 'best_practices',
          label: 'Microsoft Cloud Security Benchmark v1',
          apiKey: 'Microsoft Cloud Security Benchmark v1',
        },
      ],
      standardsMapping: {
        total: { riskApi: 'all', detailApi: null },
        iso_27001: { riskApi: 'iso', detailApi: 'iso_27001' },
        cis: { riskApi: 'cis', detailApi: 'cis' },
        best_practices: { riskApi: 'best_practices', detailApi: 'best_practices' },
      },
      authorityPrefix: 'crowdstrike_azure_',
    },
  
    gcp: {
      label: 'Google Cloud Platform',
      cloud: 'gcp',
      standards: [
        { id: 'total', label: 'GCP 合規總表', apiKey: 'total' },
        { id: 'iso_27001', label: 'ISO 27001:2022', apiKey: 'ISO 27001:2022' },
        { id: 'cis', label: 'CIS 4.0.0 GCP Foundation', apiKey: 'CIS 4.0.0 GCP Foundation' },
      ],
      standardsMapping: {
        total: { riskApi: 'all', detailApi: null },
        iso_27001: { riskApi: 'iso', detailApi: 'iso_27001' },
        cis: { riskApi: 'cis', detailApi: 'cis' },
      },
      authorityPrefix: 'crowdstrike_gcp_',
    },
  
    aws: {
      label: 'Amazon Web Services',
      cloud: 'aws',
      standards: [
        { id: 'total', label: 'AWS 合規總表', apiKey: 'total' },
        { id: 'iso_27001', label: 'ISO 27001:2022', apiKey: 'ISO 27001:2022' },
        { id: 'cis', label: 'CIS 5.0.0 AWS Foundations', apiKey: 'CIS 5.0.0 AWS Foundations' },
      ],
      standardsMapping: {
        total: { riskApi: 'all', detailApi: null },
        iso_27001: { riskApi: 'iso', detailApi: 'iso_27001' },
        cis: { riskApi: 'cis', detailApi: 'cis' },
      },
      authorityPrefix: 'crowdstrike_aws_',
    }
  };

  const config = PLATFORM_CONFIG[platform?.toLowerCase() || 'azure'];
  const cloud = config.cloud;
  const scopeConfig = useMemo(() => getCloudScopeConfig(cloud), [cloud]);
  const standards = config.standards;
  const standardsMapping = config.standardsMapping;

  // 確認授權與角色權限
  const availableStandardIds = useMemo(() => {
    if (!crowdStrikeEnabled || !authorityCodes || authorityCodes.length === 0) return [];
  
    // const prefix = `${source}_${cloud}_`; // crowdstrike_azure_
    const prefix = config.authorityPrefix;

    return authorityCodes
      .filter(code => code.startsWith(prefix))
      .map(code => {
        // const lastPart = code.split("_").pop();
        // if (lastPart === "summary") return "total";
        // if (lastPart === "iso27001") return "iso_27001";
        // return lastPart;

        // crowdstrike_azure_best_practice → best_practice（不可只用 pop，會變成 practice）
        const suffix = code.slice(prefix.length);
        if (suffix === 'summary') return 'total';
        if (suffix === 'iso27001') return 'iso_27001';
        if (suffix === 'best_practice') return 'best_practices';
        return suffix; // cis
      })
      .filter(id => standards.some(std => std.id === id));
  // }, [authorityCodes, standards, crowdStrikeEnabled]);
  }, [authorityCodes, standards, crowdStrikeEnabled, config.authorityPrefix]);


  // 月份搜尋條件
  const getLastFiveMonths = () => {
    const result = [];
    const now = new Date();

    for (let i = 0; i < 5; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const label = i === 0
        ? `${year}/${month}(當月)`
        : `${year}/${month}`;

      result.push({
        value: `${year}-${String(month).padStart(2, '0')}`,
        label
      });
    }
    return result;
  };

  const monthOptions = getLastFiveMonths();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [loading, setLoading] = useState(false);

  // 切換雲別時清掉舊範圍；月份變更時依當月重抓 account 清單
  useEffect(() => {
    dispatch(resetCloudScopes());
  }, [dispatch, cloud]);

  useEffect(() => {
    dispatch(fetchCloudScopes({ platform: cloud, month: selectedMonth }));
  }, [dispatch, cloud, selectedMonth]);

  const handleCloudScopeChange = (scopeId) => {
    dispatch(setSelectedCloudScope(scopeId));
  };

  // CloudScopeSelector 原版 value || allValue；CrowdStrike 不提供「全部」，需確保有真實 account id
  const effectiveCloudScope = selectedCloudScope || cloudScopes[0]?.id || '';

  const downloadFile = (fileName, fileString) => {
    const mimeType = fileName?.endsWith('.zip')
      ? 'application/zip'
      : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const linkSource = `data:${mimeType};base64,${fileString}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const handleExport = async () => {
    const cloudAccountId = (effectiveCloudScope || '').trim();
    if (!cloudAccountId || cloudAccountId === 'all') {
      toastAlert.autoClose(true).showError('請先選擇查詢範圍');
      return;
    }

    setLoading(true);
    const projectUuid = localStorage.getItem("project_uuid");
    const payload = {
      project_uuid: projectUuid,
      month: selectedMonth,
      standard: selectedStandard,
      cloud: cloud,
      cloud_account_id: cloudAccountId,
    };
    try {
      let downloadApi = DownloadCrowdStrikeIsoWord;
      if (selectedStandard === 'cis') {
        downloadApi = DownloadCrowdStrikeCisWord;
      } else if (selectedStandard === 'best_practices') {
        downloadApi = DownloadCrowdStrikeBestPractices;
      }
      const res = await downloadApi(payload);
      const data = res?.data;

      if (data?.error_message) {
        toastAlert.autoClose(true).showError(data.error_message);
        return;
      }

      if (!data || data.queryError === true || data.success === false) {
        toastAlert.autoClose(true).showError(`「${selectedMonth}」目前無資料`);
        return;
      }

      const fileName = data.zipName || data.wordName;
      const fileString = data.zipString || data.wordString;

      if (fileName && fileString) {
        downloadFile(fileName, fileString);
        toastAlert.autoClose(true).showSuccess('下載成功');
      } else {
        toastAlert.autoClose(true).showError(`「${selectedMonth}」目前無資料`);
      }

    } catch (error) {
      if (error.__handled) return;
      // 尚未執行排程前，應顯示目前無資料
      const msg =
        error?.response?.data?.error_message ||
        error?.response?.data?.msg ||
        `「${selectedMonth}」目前無資料`;
      toastAlert.autoClose(true).showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStandardChange = (e) => {
    const standardId = e.target.value;
    const mapping = standardsMapping[standardId];
    dispatch(setSelectedStandard(standardId));  
  };
  
  return (
    <React.Fragment>
      <Container className="crowdstrikeOverview mt-2">
        <Row className="align-items-center mb-4">
          <Col className="mt-3">
            <h4 style={{ fontWeight: 'bold' }}>{config.label}</h4>
          </Col>
          <Col xs={6} md={2}>
            <Form>
              <Form.Group>
                <Form.Label style={{ fontWeight: 'bold' }}>
                  搜尋條件
                </Form.Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </Form.Group>
            </Form>
          </Col>
          {scopeConfig.enabled && (
            <Col xs={6} md={2}>
              <Form>
                <CloudScopeSelector
                  label={scopeConfig.label}
                  value={effectiveCloudScope}
                  options={cloudScopes}
                  loading={scopesLoading}
                  onChange={handleCloudScopeChange}
                  includeAll={false}
                />
              </Form>
            </Col>
          )}
          <Col xs={12} md={3}>
            <Form>
              <Form.Group>
                <Form.Label>
                  <span style={{ fontWeight: 'bold' }}>項目名稱</span>
                </Form.Label>
                <Input type="select" bsSize="sm" value={selectedStandard} onChange={handleStandardChange}>
                {standards
                    .filter(std => availableStandardIds.includes(std.id))
                    .map((std) => (
                      <option key={std.id} value={std.id}>
                        {std.label}
                      </option>
                  ))}
                </Input>
              </Form.Group>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col className="d-flex justify-content-end">
            <button
              className="export-btn"
              onClick={handleExport}
              disabled={loading || scopesLoading || !effectiveCloudScope}
            >
              <Image
                alt="download"
                src="/assets/images/ic_download_24dp_323232_FILL0_wght400_GRAD0_opsz24.svg"
                style={{ display: 'inline' }}
              />
              <span style={{ color: '#323232' }}>輸出每月報表</span>
            </button>
          </Col>
          {loading && (
            <div className="d-flex justify-content-center my-3">
              <Spinner animation="border" role="status" />
            </div>
          )}
        </Row>
      </Container>
    </React.Fragment>
  );
}
